import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, inr, customerSession } from "@/lib/api";
import { CustomerHeader, RatingStars, EmptyState } from "@/components/tabdine/Shared";
import { Button } from "@/components/ui/button";
import { FileText } from "lucide-react";

export default function CustomerProfile() {
  const nav = useNavigate();
  const session = customerSession.get();
  const [tab, setTab] = useState("loyalty");
  const [orders, setOrders] = useState([]);
  const [loyalty, setLoyalty] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    if (!session) {
      nav("/login");
      return;
    }
    api.get("/customer/orders").then((r) => setOrders(r.data));
    api.get("/customer/loyalty").then((r) => setLoyalty(r.data));
    api.get("/customer/my-reviews").then((r) => setReviews(r.data));
    // eslint-disable-next-line
  }, []);

  if (!session) return null;

  return (
    <div className="min-h-screen bg-brand-light">
      <CustomerHeader />
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-brand-dark" data-testid="profile-name">{session.user?.name}</h1>
            <div className="text-sm text-brand-dark/60">{session.user?.phone}</div>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              customerSession.clear();
              nav("/");
            }}
            data-testid="logout"
          >
            Sign out
          </Button>
        </div>

        <div className="mt-6 flex gap-2 text-sm">
          {[
            ["loyalty", "Loyalty"],
            ["orders", "Orders"],
            ["reviews", "My Reviews"],
          ].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`profile-tab-${k}`}
              className={`px-4 py-2 rounded-full ${tab === k ? "bg-brand-dark text-brand-light" : "bg-white text-brand-dark/70 border border-brand-dark/10"}`}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="mt-4">
          {tab === "loyalty" && (
            loyalty.length === 0 ? (
              <EmptyState title="No loyalty cards yet" subtitle="Visit a Tabdine restaurant to earn your first stamp." />
            ) : (
              <div className="grid gap-3">
                {loyalty.map((b) => (
                  <div key={b.restaurant_id} className="bg-white rounded-2xl border border-brand-dark/5 p-4" data-testid={`loyalty-card-${b.restaurant_id}`}>
                    <div className="flex items-center gap-3">
                      <img src={b.restaurant?.logo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                      <div className="flex-1">
                        <div className="font-heading font-semibold text-brand-dark">{b.restaurant?.name}</div>
                        <div className="text-xs text-brand-dark/60">{b.config?.reward_description}</div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="text-xs text-brand-dark/60 mb-1">
                        {b.stamps_earned} / {b.config?.stamps_required} stamps
                      </div>
                      <div className="grid grid-cols-10 gap-1">
                        {Array.from({ length: b.config?.stamps_required || 10 }).map((_, i) => (
                          <div
                            key={i}
                            className={`aspect-square rounded-full border-2 ${
                              i < b.stamps_earned ? "bg-brand-orange border-brand-orange" : "border-brand-dark/10"
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {tab === "orders" && (
            orders.length === 0 ? (
              <EmptyState title="No orders yet" />
            ) : (
              <div className="space-y-2">
                {orders.map((o) => {
                  const paid = o.status === "paid" || o.payment_status === "paid";
                  const cancelled = o.status === "cancelled";
                  return (
                    <div
                      key={o.id}
                      className={`bg-white rounded-2xl border p-4 ${cancelled ? "border-red-200" : "border-brand-dark/5 hover:border-brand-orange/30"}`}
                      data-testid={`order-${o.id}`}
                    >
                      <Link to={`/order-confirm?order=${o.id}`} className="block">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-heading font-semibold">{o.restaurant_name}</div>
                            <div className="text-xs text-brand-dark/60">
                              {o.order_no} · {o.items.length} items · {(o.created_at || "").slice(0, 10)}
                              <span className="ml-1 inline-block bg-brand-orange/10 text-brand-orange px-1.5 py-0.5 rounded text-[10px] uppercase">
                                {o.order_type === "takeaway" ? "Takeaway" : "Dine-In"}
                              </span>
                            </div>
                            {cancelled && o.cancellation_reason && (
                              <div className="text-xs text-red-600 mt-1">Cancelled: {o.cancellation_reason}</div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="font-heading font-semibold">{inr(o.total_amount)}</div>
                            <div className={`text-xs uppercase tracking-widest ${cancelled ? "text-red-600" : "text-brand-orange"}`}>
                              {o.status}
                            </div>
                          </div>
                        </div>
                      </Link>
                      {paid && (
                        <Link
                          to={`/invoice?order=${o.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-2 inline-flex items-center gap-1 text-xs text-brand-orange hover:underline"
                          data-testid={`invoice-link-${o.id}`}
                        >
                          <FileText size={12} /> View invoice
                        </Link>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          )}

          {tab === "reviews" && (
            reviews.length === 0 ? (
              <EmptyState title="No reviews yet" />
            ) : (
              <div className="space-y-2">
                {reviews.map((r) => (
                  <div key={r.id} className="bg-white rounded-2xl border border-brand-dark/5 p-4" data-testid={`myreview-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-heading font-semibold">{r.restaurant?.name}</div>
                      <RatingStars value={r.rating} />
                    </div>
                    <div className="text-sm text-brand-dark/80 mt-1">{r.comment}</div>
                    <div className="text-xs text-brand-dark/50 mt-1">{(r.created_at || "").slice(0, 10)}</div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
